/**
 * useWorkFeed — the cross-desk Work index data source.
 *
 * Wraps the same endpoints the legacy work list reads (`GET /api/work-items`
 * for the items, `GET /api/desks` for desk names), and exposes a flat
 * `WorkItem[]` plus a `deskName(deskId)` resolver so the Work surface can group
 * by desk and stamp each card with the desk's accent. We do NOT re-implement
 * any list/sort logic here — that lives in workLifecycle.ts and the components.
 *
 * WORK LISTS EXACTLY THE SIDEBAR'S TEAMS (`readableDeskRows`): an item on a
 * Team the sidebar does not list is dropped here, before anything folds or
 * counts it, so chips, counts, groups and the pager all agree. An item on no
 * Team stays.
 *
 * Refetch is wired to the same `work_item.updated` / `tickets.invalidate`
 * window events the list listens to, so resolving a gate elsewhere reflows the
 * list. The hook never mutates anything; all writes go through the gate hook.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { WorkItem } from "@yagni/shared";
import { useAuthSession } from "../hooks/useWorkspaceRole.js";
import { readableDeskRows } from "../shell/sidebarTeams.js";
import { publishTeamWorking } from "../shell/liveTeams.js";
import { workingByTeam } from "../teams/allTeams.js";

interface DeskNameRow {
  id: string;
  name: string;
}

/** The Teams the sidebar lists, as an id→name list. */
function flattenDeskNames(payload: unknown, viewerUserId: string | null): DeskNameRow[] {
  return readableDeskRows(payload, viewerUserId).flatMap((r) =>
    typeof r.id === "string" && typeof r.name === "string" ? [{ id: r.id, name: r.name }] : [],
  );
}

export interface UseWorkFeedResult {
  /** ALL items, archived included — callers partition on `archivedAt`. */
  items: WorkItem[];
  /** Resolve a desk id to a display name; falls back to "Unassigned". */
  deskName: (deskId: string | null) => string;
  /**
   * False only until the FIRST fetch settles; refetches keep it true so the
   * previous items stay on screen (keep-previous-data, mirroring the Feed's
   * `data!==null` contract). The shell gates its loading skeleton on `!ready`,
   * never on `loading`, so an event-driven background refetch (gate resolve,
   * archive, dismiss) never tears down the mounted right-rail @yagni chat.
   */
  ready: boolean;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * POST an archive/unarchive flip, then reflow every Work listener. A refused
 * flip throws: the caller owns telling the reader, because a silent failure
 * just brings the row back on the next refetch with no word why. The reflow
 * fires either way so the surface re-reads the truth.
 */
async function postArchiveFlip(id: string, verb: "archive" | "unarchive"): Promise<void> {
  try {
    const res = await fetch(`/api/work-items/${encodeURIComponent(id)}/${verb}`, {
      method: "POST",
      credentials: "include",
    });
    if (!res.ok) throw new Error(`Could not ${verb} the work item (${res.status})`);
  } finally {
    window.dispatchEvent(new CustomEvent("tickets.invalidate"));
  }
}

export function archiveWorkItem(id: string): Promise<void> {
  return postArchiveFlip(id, "archive");
}

export function unarchiveWorkItem(id: string): Promise<void> {
  return postArchiveFlip(id, "unarchive");
}

/** The resolver's answer for a NULL desk or one missing from its name map. */
export const UNASSIGNED = "Unassigned";

/** How often a visible Work list re-reads the server (P1.4 resync floor). */
export const WORK_FEED_RESYNC_MS = 60_000;

export function useWorkFeed(): UseWorkFeedResult {
  const viewerUserId = useAuthSession()?.user?.id ?? null;
  const [items, setItems] = useState<WorkItem[]>([]);
  const [deskNames, setDeskNames] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  // Write-once-true: flips on the first settle (success OR error) and never
  // resets, so refetches keep the previous items mounted instead of flashing
  // the surface skeleton.
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requestSequence = useRef(0);
  const fetchAll = useCallback(() => {
    const sequence = ++requestSequence.current;
    setLoading(true);
    setError(null);
    Promise.all([
      // `archived=true` = no archive filter: the surface partitions client-side
      // so the selected item survives the undo window, deep links into archived
      // items keep working, and the archived disclosure is free.
      fetch("/api/work-items?archived=true&surface=team", { credentials: "include" })
        .then((res) => {
          if (!res.ok) throw new Error(`Could not load your tickets (${res.status})`);
          return res.json();
        })
        .then((data: WorkItem[] | { tickets: WorkItem[] }) =>
          Array.isArray(data) ? data : (data.tickets ?? []),
        ),
      // Null when the Team list could not be read: the items then stay
      // unscoped rather than Work going blank on a transient failure.
      fetch("/api/desks", { credentials: "include" })
        .then((res) => (res.ok ? res.json() : null))
        .then((data: unknown) => (data == null ? null : flattenDeskNames(data, viewerUserId)))
        .catch(() => null),
    ])
      .then(([itemData, deskRows]) => {
        if (sequence !== requestSequence.current) return;
        const teams = deskRows ? new Set(deskRows.map((d) => d.id)) : null;
        const visible = teams
          ? itemData.filter((item) => item.deskId == null || teams.has(item.deskId))
          : itemData;
        setItems(visible);
        // The rail's Working counts read the same list.
        publishTeamWorking(workingByTeam(visible));
        setDeskNames(new Map((deskRows ?? []).map((d) => [d.id, d.name])));
      })
      .catch((err: unknown) => {
        if (sequence !== requestSequence.current) return;
        setError(err instanceof Error ? err.message : "Could not load your work.");
      })
      .finally(() => {
        if (sequence !== requestSequence.current) return;
        setLoading(false);
        setReady(true);
      });
  }, [viewerUserId]);

  useEffect(() => {
    fetchAll();
    return () => {
      requestSequence.current++;
    };
  }, [fetchAll]);

  // Reflow when a gate resolves anywhere (board + detail panel both fire
  // these), and when a run's status flips (`runs.updated`/`runs.created` from
  // the socket) — a run stalling into a waiting state must move its item into
  // the needs-you grouping on the index without a refresh, not only inside an
  // already-open case file (operating-picture review 2026-07-19).
  useEffect(() => {
    const handler = () => fetchAll();
    window.addEventListener("work_item.updated", handler);
    window.addEventListener("tickets.invalidate", handler);
    window.addEventListener("runs.updated", handler);
    window.addEventListener("runs.created", handler);
    return () => {
      window.removeEventListener("work_item.updated", handler);
      window.removeEventListener("tickets.invalidate", handler);
      window.removeEventListener("runs.updated", handler);
      window.removeEventListener("runs.created", handler);
    };
  }, [fetchAll]);

  // Resync (P1.4): socket events are best-effort, so a dropped socket or a
  // backgrounded tab can miss a phase flip. Refetch when either socket
  // reconnects (`ws.reconnected` from useWebSocket), when the tab comes back
  // into view, and once a minute while it is visible. The requestSequence
  // guard above keeps an overlapping fetch from landing stale rows.
  useEffect(() => {
    const isVisible = () => document.visibilityState === "visible";
    const onReconnect = () => fetchAll();
    const onVisibility = () => {
      if (isVisible()) fetchAll();
    };
    const interval = setInterval(() => {
      if (isVisible()) fetchAll();
    }, WORK_FEED_RESYNC_MS);
    window.addEventListener("ws.reconnected", onReconnect);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(interval);
      window.removeEventListener("ws.reconnected", onReconnect);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [fetchAll]);

  const deskName = useCallback(
    (deskId: string | null): string => {
      if (!deskId) return UNASSIGNED;
      const name = deskNames.get(deskId);
      return name || UNASSIGNED;
    },
    [deskNames],
  );

  return useMemo(
    () => ({ items, deskName, ready, loading, error, refetch: fetchAll }),
    [items, deskName, ready, loading, error, fetchAll],
  );
}
